'use client'

import { useState } from 'react'
import { signIn } from 'next-auth/react'
import { useRouter, useSearchParams } from 'next/navigation'
import { Loader2 } from 'lucide-react'
import { MarvinTeken } from '@/components/MarvinTeken'
import { Suspense } from 'react'

function MicrosoftIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 21 21" fill="none">
      <rect x="1" y="1" width="9" height="9" fill="#F25022" />
      <rect x="11" y="1" width="9" height="9" fill="#7FBA00" />
      <rect x="1" y="11" width="9" height="9" fill="#00A4EF" />
      <rect x="11" y="11" width="9" height="9" fill="#FFB900" />
    </svg>
  )
}

function LoginForm() {
  const router = useRouter()
  const searchParams = useSearchParams()
  // Alleen een pad binnen Marvin (geen ander domein of javascript:), beveiligingsronde 22-10-2026
  const gevraagd = searchParams.get('callbackUrl') ?? '/'
  const callbackUrl = /^\/(?![/\\])/.test(gevraagd) ? gevraagd : '/'
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [msLoading, setMsLoading] = useState(false)
  const [ssoEnabled, setSsoEnabled] = useState(false)
  const [credentialsEnabled, setCredentialsEnabled] = useState(false)
  const [showCredentials, setShowCredentials] = useState(false)

  useState(() => {
    fetch('/api/sso-status')
      .then(r => r.json())
      .then((d: { enabled: boolean; credentialsEnabled: boolean }) => {
        setSsoEnabled(d.enabled)
        setCredentialsEnabled(d.credentialsEnabled)
        if (!d.enabled && d.credentialsEnabled) setShowCredentials(true)
      })
      .catch(() => setShowCredentials(true))
  })

  const handleMicrosoft = async () => {
    setMsLoading(true)
    await signIn('azure-ad', { callbackUrl })
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setLoading(true)
    const result = await signIn('credentials', { email, password, redirect: false })
    setLoading(false)
    if (result?.error) {
      setError('Ongeldig e-mailadres of wachtwoord')
    } else {
      router.push(callbackUrl)
      router.refresh()
    }
  }

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center px-4">
      <div className="w-full max-w-sm">
        <div className="flex flex-col items-center mb-8">
          <div className="w-12 h-12 rounded-2xl bg-[#46F0A4] flex items-center justify-center mb-4">
            <MarvinTeken size={24} kleur="#171717" dikte={2.1} />
          </div>
          <h1 className="text-xl font-semibold text-white">Marvin</h1>
          <p className="text-sm text-neutral-500 mt-1">Inloggen om door te gaan</p>
        </div>

        <div className="space-y-3">
          {ssoEnabled && (
            <>
              <button
                onClick={handleMicrosoft}
                disabled={msLoading}
                className="w-full flex items-center justify-center gap-2.5 px-4 py-2.5 bg-white hover:bg-neutral-100 disabled:opacity-50 text-neutral-900 text-sm font-medium rounded-lg transition-colors"
              >
                {msLoading ? <Loader2 size={14} className="animate-spin" /> : <MicrosoftIcon />}
                {msLoading ? 'Doorsturen...' : 'Inloggen met Microsoft'}
              </button>

              {credentialsEnabled && (
                <div className="flex items-center gap-3">
                  <div className="flex-1 h-px bg-neutral-800" />
                  <button
                    onClick={() => setShowCredentials(v => !v)}
                    className="text-xs text-neutral-600 hover:text-neutral-400 transition-colors"
                  >
                    of e-mail & wachtwoord
                  </button>
                  <div className="flex-1 h-px bg-neutral-800" />
                </div>
              )}
            </>
          )}

          {showCredentials && (
            <form onSubmit={handleSubmit} className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-neutral-400 mb-1.5">E-mailadres</label>
                <input
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  className="w-full px-3 py-2.5 bg-neutral-900 border border-neutral-700 rounded-lg text-sm text-white placeholder-neutral-600 focus:outline-none focus:border-neutral-500 transition-colors"
                  placeholder="naam@bedrijf.nl"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-neutral-400 mb-1.5">Wachtwoord</label>
                <input
                  type="password"
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  className="w-full px-3 py-2.5 bg-neutral-900 border border-neutral-700 rounded-lg text-sm text-white placeholder-neutral-600 focus:outline-none focus:border-neutral-500 transition-colors"
                  placeholder="••••••••"
                />
              </div>
              {error && (
                <p className="text-xs text-red-400 bg-red-950/40 border border-red-900/40 rounded-lg px-3 py-2">
                  {error}
                </p>
              )}
              <button
                type="submit"
                disabled={loading}
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm font-medium rounded-lg transition-colors"
              >
                {loading && <Loader2 size={14} className="animate-spin" />}
                {loading ? 'Inloggen...' : 'Inloggen'}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  )
}

export default function LoginPage() {
  return (
    <Suspense>
      <LoginForm />
    </Suspense>
  )
}
